import React, { useState, useRef, useEffect } from 'react';
import {
  View, Text, ScrollView, TextInput, StyleSheet, TouchableOpacity,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import * as FileSystem from 'expo-file-system';

async function readUriAsDataUri(uri) {
  if (Platform.OS === 'web') {
    const blob = await fetch(uri).then((r) => r.blob());
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  const base64 = await FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  });
  return `data:audio/m4a;base64,${base64}`;
}
import { useGame } from '../../context/GameContext';
import { theme } from '../../screens/Common.style';
import GameVoiceMessageBubble from './GameVoiceMessageBubble';
import GameVoiceMessageRecorder from './GameVoiceMessageRecorder';

const PASTEL_COLORS = [
  '#C8E6FF', // blue
  '#C8F5E0', // green
  '#FFF3C8', // yellow
  '#FFD9C8', // peach
  '#E8C8FF', // lavender
  '#FFC8E8', // pink
  '#C8F5F0', // teal
  '#FFC8C8', // rose
];

function senderColor(senderId) {
  return PASTEL_COLORS[Math.abs(senderId ?? 0) % PASTEL_COLORS.length];
}

export default function GameChatPanel({ messages }) {
  const { currentUserId, sendMessage, room, currentRound } = useGame();
  const [text, setText] = useState('');
  const scrollRef = useRef(null);

  const isDiscussion = currentRound?.status === 'DISCUSSION';

  useEffect(() => {
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [messages]);

  const handleSendText = async () => {
    const msg = text.trim();
    if (!msg) return;
    setText('');
    try {
      await sendMessage({ text: msg });
    } catch {}
  };

  const handleSendVoice = async ({ uri, duration }) => {
    try {
      const audioUrl = await readUriAsDataUri(uri);
      await sendMessage({ audioUrl, duration });
    } catch (e) {
      console.warn('Voice send failed', e);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={100}
    >
      <ScrollView
        ref={scrollRef}
        style={styles.messages}
        contentContainerStyle={styles.messagesContent}
      >
        {messages.length === 0 && (
          <Text style={styles.emptyText}>La discussion commence…</Text>
        )}
        {messages.map((msg) => {
          const mine = msg.senderId === currentUserId;
          const color = mine ? null : senderColor(msg.senderId);
          if (msg.audioUrl) {
            return (
              <GameVoiceMessageBubble
                key={msg.id}
                audioUrl={msg.audioUrl}
                duration={msg.duration}
                senderName={msg.sender?.firstName}
                mine={mine}
                bubbleColor={color}
              />
            );
          }
          return (
            <View
              key={msg.id}
              style={[
                styles.bubble,
                mine ? styles.bubbleMine : styles.bubbleTheirs,
                !mine && { backgroundColor: color },
              ]}
            >
              {!mine && <Text style={styles.senderName}>{msg.sender?.firstName}</Text>}
              <Text style={[styles.msgText, mine && styles.msgTextMine]}>{msg.text}</Text>
            </View>
          );
        })}
      </ScrollView>

      {isDiscussion && (
        <View style={styles.inputBar}>
          <TextInput
            style={styles.input}
            placeholder="Écrire un message…"
            value={text}
            onChangeText={setText}
            onSubmitEditing={handleSendText}
            returnKeyType="send"
            multiline={false}
          />
          {text.trim() ? (
            <TouchableOpacity style={styles.sendBtn} onPress={handleSendText}>
              <Text style={styles.sendBtnText}>→</Text>
            </TouchableOpacity>
          ) : (
            <GameVoiceMessageRecorder onSend={handleSendVoice} disabled={!isDiscussion} />
          )}
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  messages: { flex: 1 },
  messagesContent: { padding: 12, paddingBottom: 4 },
  emptyText: { textAlign: 'center', color: '#bbb', marginTop: 24, fontSize: 14 },
  bubble: { maxWidth: '75%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 9, marginVertical: 3 },
  bubbleMine: { backgroundColor: theme.colors.primary, alignSelf: 'flex-end' },
  bubbleTheirs: { backgroundColor: '#f0f0f0', alignSelf: 'flex-start' },
  senderName: { fontSize: 11, color: '#888', marginBottom: 2 },
  msgText: { fontSize: 15, color: '#333' },
  msgTextMine: { color: '#fff' },
  inputBar: { flexDirection: 'row', alignItems: 'center', borderTopWidth: 1, borderTopColor: '#eee', paddingHorizontal: 12, paddingVertical: 8, gap: 8 },
  input: { flex: 1, borderWidth: 1, borderColor: '#ddd', borderRadius: 24, paddingHorizontal: 16, paddingVertical: 8, fontSize: 15, maxHeight: 100 },
  sendBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: theme.colors.primary, alignItems: 'center', justifyContent: 'center' },
  sendBtnText: { color: '#fff', fontSize: 20, lineHeight: 22 },
});
